// Suggestions (SPEC §23; route #/suggestions[/<status filter>]): the team's notebook of improvements
// to the application, in a drawer open to every logged-in person.
// - Composer at the top: a title (≤ 120), then (unfolded once used) a description (≤ 10 000) and
//   PROPOSER (POST /api/suggestions).
// - Filter chips with their counts (Toutes · Proposées · Approuvées · En cours · Réalisées · Refusées)
//   and a search box; an admin opens on « Proposées » when some wait for a decision.
// - One row per suggestion: title, status badge, author (admins), date, the text (folded when long),
//   the admin's decision with its comment, the work note (commit, version).
// - Admins: APPROUVER / REFUSER and the progress actions in a separate band (an optional comment or
//   note is asked first), Modifier and Supprimer in the row menu. The author edits or deletes their
//   own suggestion while it is proposed.
// The list is the store's `suggestions` slice (the shell loads it and applies the live events); the
// panel reloads it on open and applies the events too, so it stays live on its own (the store
// ignores a copy it already holds). Text written by people is always shown as text.
import { h, button, iconButton, icon, field, input, badge, chip, emptyState, spinner, uid } from '../dom.js';
import { t } from '../i18n/fr.js';
import { relativeTime, formatDateTime } from '../format.js';
import { countedTextarea, formError, setBusy } from './admin/common.js';
import { childLayers } from './automations/widgets.js';
import {
  FILTERS, STATUSES, TITLE_MAX, TEXT_MAX, COMMENT_MAX, NOTE_MAX, statusTone, resolveFilter, defaultFilter, suggestionOf,
  suggestionsOf, countByStatus, filterSuggestions, isMine, isAdminUser, canEdit, canDelete, adminActions, actionRequest,
  shortCommit, suggestionBody, validateSuggestion, changedFields,
} from './suggestions/model.js';
import './suggestions/strings.js';

const SEARCH_DEBOUNCE_MS = 150;
/** Relative dates (« il y a 5 min ») are refreshed every minute while the panel is open. */
const MINUTE_MS = 60_000;
/** A long text is folded above this many characters. */
const FOLD_CHARS = 320;

/** What each admin action asks first (an optional comment or note): title, label, hint, length. */
const ACTION_PROMPTS = Object.freeze({
  approve: { title: 'sug.prompt.approveTitle', label: 'sug.prompt.comment', hint: 'sug.prompt.approveHint', max: COMMENT_MAX },
  reject: { title: 'sug.prompt.rejectTitle', label: 'sug.prompt.comment', hint: 'sug.prompt.rejectHint', max: COMMENT_MAX },
  start: { title: 'sug.prompt.startTitle', label: 'sug.prompt.note', hint: 'sug.prompt.startHint', max: NOTE_MAX },
  finish: { title: 'sug.prompt.finishTitle', label: 'sug.prompt.note', hint: 'sug.prompt.finishHint', max: NOTE_MAX },
  reopen: { title: 'sug.prompt.reopenTitle', label: 'sug.prompt.note', hint: 'sug.prompt.reopenHint', max: NOTE_MAX },
});
const ACTION_ICONS = Object.freeze({ approve: 'check', reject: 'close', start: 'clock', finish: 'check', reopen: 'refresh' });
/** The main action of each status is the black pill; the other ones stay quieter. */
const PRIMARY_ACTION = Object.freeze({ proposed: 'approve', approved: 'start', in_progress: 'finish' });

function actionVariant(status, action) {
  if (PRIMARY_ACTION[status] === action) return 'primary';
  if (status === 'proposed' || status === 'rejected' || (status === 'approved' && action === 'finish')) return 'secondary';
  return 'ghost';
}

/**
 * Opens the suggestions drawer.
 * @param {object} ctx app context
 * @param {string|null} [tab] initial status filter (all · proposed · approved · in_progress · done ·
 *   rejected); an unknown or missing one opens the default (an admin: « Proposées » when some wait)
 * @returns {import('./overlay.js').Layer}
 */
export function openSuggestions(ctx, tab) {
  const me = () => ctx.store.get().me;
  const isAdmin = () => isAdminUser(me());
  const list = () => ctx.store.get().suggestions || [];
  const loaded = ctx.store.get();
  const st = {
    filter: resolveFilter(tab) || defaultFilter(list(), isAdmin()),
    query: '',
    status: loaded.suggestionsUnavailable ? 'unavailable' : loaded.suggestionsLoaded ? 'ok' : 'loading',
    error: '',
    expanded: new Set(),   // ids whose long text is unfolded
    busy: new Set(),       // ids with a request on the way
    sending: false,
  };
  // Without a filter in the route, the default one waits for the list (an admin: « Proposées »).
  let autoFilter = !resolveFilter(tab);
  let layer = null;
  let children = null;
  let searchTimer = null;
  let seq = 0;
  const offs = [];
  const now = () => Date.now() + (Number(ctx.store.get().serverOffset) || 0);
  const fk = (el, key) => { el.dataset.fk = key; return el; };

  // --- composer ----------------------------------------------------------------------------------------
  const titleInput = input({ maxLength: TITLE_MAX, placeholder: t('sug.form.titlePlaceholder'), autocomplete: 'off' });
  const textArea = countedTextarea({ max: TEXT_MAX, rows: 4, placeholder: t('sug.form.textPlaceholder') });
  const titleField = field(t('sug.form.title'), titleInput);
  const textField = field(t('sug.form.text'), textArea, { hint: t('sug.form.textHint') });
  const composeError = formError();
  const submitBtn = button(t('sug.submit'), { variant: 'primary', size: 'sm', icon: 'send', type: 'submit', onClick: (e) => { e.preventDefault(); submit(); } });
  // Enter in the title goes on to the description; Ctrl (⌘) + Enter there sends.
  titleInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); textArea.input.focus(); }
  });
  textArea.input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.isComposing) { e.preventDefault(); submit(); }
  });
  const composeTitleId = uid('sug-compose');
  // The description and PROPOSER unfold once the composer is used, so the list starts high.
  const composeMore = h('div', { class: 'sug-composer-more' }, textField, composeError,
    h('div', { class: 'sug-composer-foot' }, h('span', { class: 'sug-shortcut muted' }, t('sug.form.shortcut')), submitBtn));
  const composer = h('form', { class: 'sug-composer', novalidate: true, 'aria-labelledby': composeTitleId, onSubmit: (e) => { e.preventDefault(); submit(); } },
    h('h3', { class: 'sug-composer-title', id: composeTitleId }, icon('lightbulb', { size: 18 }), h('span', {}, t('sug.compose.title'))),
    titleField, composeMore);
  const setComposeOpen = (open) => { composeMore.hidden = !open; };
  composer.addEventListener('focusin', () => setComposeOpen(true));
  titleInput.addEventListener('input', () => setComposeOpen(true));
  composer.addEventListener('focusout', (e) => {
    if (composer.contains(e.relatedTarget) || st.sending || titleInput.value.trim() || textArea.input.value.trim()) return;
    titleField.setError(null);
    textField.setError(null);
    composeError.set('');
    setComposeOpen(false);
  });
  setComposeOpen(false);

  // --- toolbar -----------------------------------------------------------------------------------------
  const chipsHost = h('div', { class: 'sug-filters', role: 'group', 'aria-label': t('sug.filtersLabel') });
  const searchInput = h('input', {
    type: 'search', class: 'input input-sm sug-search', placeholder: t('sug.search'), 'aria-label': t('sug.searchLabel'),
    autocomplete: 'off', spellcheck: false,
  });
  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { st.query = searchInput.value.trim(); render(); }, SEARCH_DEBOUNCE_MS);
  });
  const toolbar = h('div', { class: 'sug-toolbar' }, chipsHost, searchInput);
  const listHost = h('div', { class: 'sug-listhost' });
  const root = h('div', { class: 'sug-root' }, composer, toolbar, listHost);

  function setFilter(f) {
    autoFilter = false;
    st.filter = f;
    render();
  }

  // --- rows --------------------------------------------------------------------------------------------
  function dateEl(v) {
    return h('time', { class: 'sug-date', dateTime: String(v), title: formatDateTime(v, now()) }, relativeTime(v, now()));
  }

  function statusLabel(status) {
    return STATUSES.includes(status) ? t(`sug.status.${status}`) : String(status || '');
  }

  function textBlock(s) {
    const text = String(s.text || '');
    if (!text) return null;
    if (text.length <= FOLD_CHARS) return h('p', { class: 'sug-text' }, text);
    const open = st.expanded.has(s.id);
    const toggle = fk(button(open ? t('sug.showLess') : t('sug.showMore', { n: text.length }), {
      variant: 'link', size: 'sm', class: 'sug-fold',
      onClick: () => { if (!st.expanded.delete(s.id)) st.expanded.add(s.id); render(); },
    }), `fold:${s.id}`);
    toggle.setAttribute('aria-expanded', String(open));
    return h('div', { class: 'sug-textwrap' },
      h('p', { class: ['sug-text', { 'is-folded': !open }] }, open ? text : `${text.slice(0, FOLD_CHARS).trimEnd()}…`), toggle);
  }

  /** The admin's decision (who, when) and its comment. */
  function reviewBlock(s) {
    const r = s.review;
    if (!r || (r.decision !== 'approved' && r.decision !== 'rejected')) return null;
    return h('div', { class: ['sug-note', `is-${r.decision}`] },
      h('p', { class: 'sug-note-head' },
        h('span', { class: 'mono-label' }, t(`sug.review.${r.decision}`, { name: r.byName || t('sug.unknownAuthor') })),
        r.at ? dateEl(r.at) : null),
      r.comment ? h('p', { class: 'sug-note-text' }, r.comment) : null);
  }

  /**
   * Where the work stands (in progress, done, given back) with its note, commit and version; only
   * while it matches the status (a later decision opens a new cycle).
   */
  function workBlock(s) {
    const w = s.work;
    if (!w || w.status !== s.status || !['in_progress', 'done', 'approved'].includes(w.status)) return null;
    const commit = shortCommit(w.commit);
    const head = [t(`sug.work.${w.status}`), w.byName, w.via === 'mcp' ? t('sug.work.viaMcp') : ''].filter(Boolean).join(' · ');
    const refs = [
      commit ? h('code', { class: 'sug-code', title: t('sug.work.commitTitle', { commit: w.commit }) }, commit) : null,
      w.version ? h('span', { class: 'sug-version' }, t('sug.work.version', { version: w.version })) : null,
    ].filter(Boolean);
    return h('div', { class: ['sug-note', 'is-work', `is-${w.status}`] },
      h('p', { class: 'sug-note-head' }, h('span', { class: 'mono-label' }, head), w.at ? dateEl(w.at) : null),
      w.note ? h('p', { class: 'sug-note-text' }, w.note) : null,
      refs.length ? h('p', { class: 'sug-refs' }, refs) : null);
  }

  /** Admins: the decision or follow-up actions of the status, in a band of their own. */
  function adminBand(s) {
    const actions = adminActions(s, me());
    if (!actions.length) return null;
    const label = s.status === 'proposed' || s.status === 'rejected' ? t('sug.decisionLabel') : t('sug.followLabel');
    const busy = st.busy.has(s.id);
    return h('div', { class: 'sug-admin', role: 'group', 'aria-label': `${label} — ${s.title || ''}` },
      h('span', { class: 'sug-admin-label mono-label' }, icon('shield', { size: 14 }), h('span', {}, label)),
      h('div', { class: 'sug-admin-actions' }, actions.map((a) => fk(button(t(`sug.action.${a}`), {
        variant: actionVariant(s.status, a), size: 'sm', icon: ACTION_ICONS[a], disabled: busy, onClick: () => runAction(s, a),
      }), `a:${s.id}:${a}`))));
  }

  /** Members: what happens next and, on their own proposed suggestion, Modifier and Supprimer. */
  function authorBand(s) {
    const hint = ['proposed', 'approved', 'done'].includes(s.status) ? t(`sug.memberHint.${s.status}`) : '';
    const editable = canEdit(s, me());
    if (!hint && !editable) return null;
    const busy = st.busy.has(s.id);
    return h('div', { class: 'sug-foot' },
      hint ? h('p', { class: 'sug-hint' }, hint) : null,
      editable ? h('div', { class: 'sug-foot-actions' },
        fk(button(t('sug.edit'), { variant: 'ghost', size: 'sm', icon: 'pencil', disabled: busy, onClick: () => openEditor(s) }), `e:${s.id}`),
        canDelete(s, me()) ? fk(button(t('sug.delete'), { variant: 'ghost', size: 'sm', icon: 'close', class: 'sug-del', disabled: busy, onClick: () => remove(s) }), `d:${s.id}`) : null)
        : null);
  }

  /** Admins: Modifier and Supprimer behind « … ». */
  function rowMenu(s) {
    if (!isAdmin()) return null;
    let moreBtn = null;
    moreBtn = fk(iconButton('dots', `${t('sug.more')} — ${s.title || ''}`, {
      variant: 'ghost', class: 'sug-more', disabled: st.busy.has(s.id),
      onClick: () => ctx.overlay.menu(moreBtn, [
        { label: t('sug.edit'), icon: 'pencil', onSelect: () => openEditor(s) },
        { separator: true },
        { label: t('sug.delete'), icon: 'close', danger: true, onSelect: () => remove(s) },
      ], { label: t('sug.more') }),
    }), `m:${s.id}`);
    moreBtn.setAttribute('aria-haspopup', 'menu');
    return moreBtn;
  }

  function row(s) {
    const admin = isAdmin();
    const mine = isMine(s, me());
    const known = STATUSES.includes(s.status) ? s.status : null;
    const author = mine ? t('sug.mine') : (s.authorName || t('sug.unknownAuthor'));
    return h('li', { class: ['sug-item', known && `is-${known}`, { 'is-busy': st.busy.has(s.id) }], dataset: { id: s.id } },
      h('div', { class: 'sug-item-head' },
        h('h4', { class: 'sug-item-title' }, s.title || '—'),
        badge(statusLabel(s.status), statusTone(s.status)),
        rowMenu(s)),
      h('p', { class: 'sug-meta' },
        admin ? h('span', { class: 'sug-author' }, icon('user', { size: 13 }), h('span', {}, author)) : null,
        s.createdAt ? dateEl(s.createdAt) : null),
      textBlock(s),
      reviewBlock(s),
      workBlock(s),
      admin ? adminBand(s) : authorBand(s));
  }

  // --- states ------------------------------------------------------------------------------------------
  function content() {
    if (st.status === 'loading') return h('div', { class: 'sug-state' }, spinner(t('sug.loading')), h('span', { class: 'muted' }, t('sug.loading')));
    if (st.status === 'unavailable') return emptyState({ icon: 'info', title: t('common.soon'), text: t('sug.unavailable') });
    if (st.status === 'error') {
      return emptyState({
        icon: 'alert', title: t('common.error'), text: st.error,
        action: fk(button(t('sug.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => load() }), 'retry'),
      });
    }
    const all = list();
    if (!all.length) {
      return isAdmin()
        ? emptyState({ icon: 'lightbulb', title: t('sug.empty.adminTitle'), text: t('sug.empty.adminText') })
        : emptyState({ icon: 'lightbulb', title: t('sug.empty.memberTitle'), text: t('sug.empty.memberText') });
    }
    const shown = filterSuggestions(all, { filter: st.filter, query: st.query });
    if (!shown.length) {
      if (st.query) return emptyState({ icon: 'search', title: t('sug.empty.search') });
      return emptyState({
        icon: 'inbox', title: t(`sug.empty.status.${st.filter}`),
        action: fk(button(t('sug.empty.showAll'), { variant: 'secondary', size: 'sm', onClick: () => setFilter('all') }), 'showAll'),
      });
    }
    return h('ul', { class: 'sug-list', 'aria-label': t('sug.listLabel') }, shown.map(row));
  }

  function renderChips() {
    const counts = countByStatus(list());
    const admin = isAdmin();
    chipsHost.replaceChildren(...FILTERS.map((f) => fk(chip(t(`sug.filter.${f}`), {
      active: st.filter === f, count: counts[f],
      // An admin's pending decisions stand out like « À VALIDER ».
      tone: f === 'proposed' && admin && counts.proposed > 0 ? 'amber' : undefined,
      onClick: () => setFilter(f),
    }), `chip:${f}`)));
  }

  let rendering = false;
  function render() {
    if (!layer?.isOpen() || rendering) return;
    rendering = true;
    try {
      const active = globalThis.document?.activeElement;
      const focusKey = active?.dataset?.fk || null;
      composer.hidden = st.status === 'unavailable';
      toolbar.hidden = st.status !== 'ok' || !list().length;
      renderChips();
      listHost.replaceChildren(content());
      if (focusKey) {
        const again = [...root.querySelectorAll('[data-fk]')].find((el) => el.dataset.fk === focusKey);
        // Without preventScroll, every re-render (event, minute tick) would scroll back to it.
        again?.focus?.({ preventScroll: true });
      }
    } finally {
      rendering = false;
    }
  }

  // --- actions -----------------------------------------------------------------------------------------
  async function submit() {
    if (st.sending) return;
    const form = { title: titleInput.value, text: textArea.input.value };
    const errors = validateSuggestion(form);
    titleField.setError(errors.title ? t(errors.title) : null);
    textField.setError(errors.text ? t(errors.text) : null);
    composeError.set('');
    setComposeOpen(true);
    if (errors.title) { titleInput.focus(); return; }
    if (errors.text) { textArea.input.focus(); return; }
    st.sending = true;
    setBusy(submitBtn, true);
    try {
      const saved = suggestionOf(await ctx.api.post('/api/suggestions', suggestionBody(form)));
      titleInput.value = '';
      textArea.input.value = '';
      textArea.input.dispatchEvent?.(new Event('input')); // the counter
      // The new suggestion must show: a filter that hides proposed ones goes back to « Toutes »,
      // and the search is cleared.
      if (st.filter !== 'all' && st.filter !== 'proposed') st.filter = 'all';
      autoFilter = false;
      clearTimeout(searchTimer);
      searchInput.value = '';
      st.query = '';
      if (saved) ctx.store.upsertSuggestion(saved);
      ctx.toast.success(t(isAdmin() ? 'sug.createdAdmin' : 'sug.created'));
      render();
    } catch (err) {
      composeError.set(err?.message || t('common.error'));
    } finally {
      st.sending = false;
      setBusy(submitBtn, false);
    }
  }

  /** An admin action: asks for the optional comment or note, then sends the review or progress. */
  async function runAction(s, action) {
    if (st.busy.has(s.id)) return;
    const p = ACTION_PROMPTS[action];
    const text = await ctx.overlay.prompt({
      title: t(p.title), label: t(p.label), hint: t(p.hint), multiline: true, required: false, maxLength: p.max,
      confirmLabel: t(`sug.action.${action}`),
    });
    if (text === null) return; // cancelled ('' = no comment)
    const req = actionRequest(s.id, action, text);
    st.busy.add(s.id);
    render();
    try {
      const saved = suggestionOf(await ctx.api.post(req.path, req.body));
      if (saved) ctx.store.upsertSuggestion(saved);
      ctx.toast.success(t(`sug.done.${action}`));
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      st.busy.delete(s.id);
      render();
    }
  }

  async function remove(s) {
    const ok = await ctx.overlay.confirm({
      title: t('sug.deleteTitle'), message: t('sug.deleteText', { title: s.title || '' }), confirmLabel: t('sug.delete'), danger: true,
    });
    if (!ok) return;
    st.busy.add(s.id);
    render();
    try {
      await ctx.api.del(`/api/suggestions/${encodeURIComponent(s.id)}`);
      ctx.store.removeSuggestion(s.id);
      ctx.toast.success(t('sug.deleted'));
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      st.busy.delete(s.id);
      render();
    }
  }

  /** The edit dialog (same fields as the composer); only the changed fields are sent. */
  function openEditor(s) {
    const titleIn = input({ value: s.title || '', maxLength: TITLE_MAX, autocomplete: 'off' });
    const area = countedTextarea({ value: s.text || '', max: TEXT_MAX, rows: 10 });
    const tf = field(t('sug.form.title'), titleIn);
    const xf = field(t('sug.form.text'), area);
    const err = formError();
    let busy = false;
    let modal = null;
    const saveBtn = button(t('sug.save'), { variant: 'primary', type: 'submit', onClick: (e) => { e.preventDefault(); save(); } });

    async function save() {
      if (busy) return;
      const form = { title: titleIn.value, text: area.input.value };
      const errors = validateSuggestion(form);
      tf.setError(errors.title ? t(errors.title) : null);
      xf.setError(errors.text ? t(errors.text) : null);
      if (errors.title || errors.text) {
        err.set(t('sug.error.fix'));
        (errors.title ? titleIn : area.input).focus();
        return;
      }
      err.set('');
      const patch = changedFields(s, form);
      if (!patch) { modal.close('confirm'); return; }
      busy = true;
      setBusy(saveBtn, true);
      modal.setBusy?.(true);
      try {
        const saved = suggestionOf(await ctx.api.patch(`/api/suggestions/${encodeURIComponent(s.id)}`, patch));
        if (saved) ctx.store.upsertSuggestion(saved);
        ctx.toast.success(t('sug.saved'));
        modal.close('confirm');
      } catch (e) {
        err.set(e?.message || t('common.error'));
      } finally {
        busy = false;
        setBusy(saveBtn, false);
        modal.setBusy?.(false);
      }
    }

    const content = h('form', { class: 'sug-form stack', novalidate: true, onSubmit: (e) => { e.preventDefault(); save(); } }, tf, xf, err);
    modal = children.add(ctx.overlay.openModal({
      title: t('sug.editTitle'), eyebrow: s.title || '', size: 'md', className: 'sug-modal', content,
      footer: [h('span', { class: 'spacer' }), button(t('common.cancel'), { variant: 'ghost', onClick: () => modal.close('cancel') }), saveBtn],
      initialFocus: titleIn,
    }));
    return modal;
  }

  // --- data --------------------------------------------------------------------------------------------
  async function load() {
    const my = ++seq;
    if (st.status !== 'ok') { st.status = 'loading'; render(); }
    try {
      const items = suggestionsOf(await ctx.api.get('/api/suggestions'));
      if (my !== seq || !layer?.isOpen()) return;
      st.status = 'ok';
      if (autoFilter) { st.filter = defaultFilter(items, isAdmin()); autoFilter = false; }
      ctx.store.setSuggestions(items);
    } catch (err) {
      if (my !== seq || !layer?.isOpen()) return;
      if (err?.status === 404) {
        st.status = 'unavailable';
        ctx.store.set({ suggestions: [], suggestionsLoaded: true, suggestionsUnavailable: true });
      } else if (st.status !== 'ok') {
        // A list already shown stays (the shell keeps it current); otherwise say what failed.
        st.status = 'error';
        st.error = err?.message || t('common.error');
      }
    }
    render();
  }

  // --- open --------------------------------------------------------------------------------------------
  layer = ctx.overlay.openDrawer({
    title: t('suggestions.title'), eyebrow: t('sug.eyebrow'), subtitle: t('sug.subtitle'), size: 'xl',
    className: 'sug-drawer', content: root,
  });
  children = childLayers(layer);

  offs.push(ctx.events.on('suggestion', (d) => {
    const s = suggestionOf(d);
    if (s) ctx.store.upsertSuggestion(s);
  }));
  offs.push(ctx.events.on('suggestion.removed', (d) => { if (d?.id) ctx.store.removeSuggestion(d.id); }));
  offs.push(ctx.store.on('suggestions', () => render()));
  offs.push(ctx.store.on('me', () => render()));

  let minuteTimer = setInterval(() => render(), MINUTE_MS);
  layer.onClose(() => {
    for (const off of offs) { try { off?.(); } catch { /* ignore */ } }
    clearTimeout(searchTimer);
    clearInterval(minuteTimer);
    minuteTimer = null;
    seq += 1;
  });

  render();
  load();
  return layer;
}
